import { TEnumShowIf } from '../models';
import { showIf } from './showIf';

type TEnumOption = {
  key: string | number,
  value: string | number,
};

// Filters individual enum options by their own show_if rule, complementing the
// field level show_if which hides the whole prop.
//
// enum_show_if is SUBTRACTIVE — it never adds options. `enum` must list every
// value the field can ever take, and each rule then takes its `values` away
// while its condition does not hold. Two consequences worth remembering:
//
//   * only values omitted from every rule are left untouched — those stay
//     visible no matter what the rules do. Values a rule does name are hidden
//     whenever it fails, which is the point of the keyword;
//   * `values` must name only the values the condition gates. Listing a value
//     that should always be available makes it disappear whenever the rule
//     fails.
//
// A value named by several rules must satisfy all of them. OR belongs inside a
// single rule, via show_if.dependency_type: 'any'.
//
// Worked example — trasti-motor-casco claimTotalValue, which offers 70% for
// every casco variant and 100% only for the workshop variant:
//
//   {
//     // the superset: both values appear here, gating happens below
//     "enum": ["70%", "100%"],
//     "default_value": "70%",
//     // field level show_if still hides the whole field for the mini variant
//     "show_if": { "fields": [{ "name": "variant",
//       "value": ["estimate", "partnership", "workshop"] }] },
//     "enum_show_if": [
//       { "values": ["100%"],
//         "show_if": { "fields": [{ "name": "variant", "value": ["workshop"] }] } }
//     ]
//   }
//
//   variant: 'workshop'    -> ['70%', '100%']   rule passes
//   variant: 'estimate'    -> ['70%']           rule fails, 100% removed
//   variant: 'mini'        -> field hidden entirely by show_if
//
// Rules read the product's own form values by default; set
// show_if.fields[].use_global_form to read the whole quote form instead.
export const filterEnumShowIf = (
  rules?: TEnumShowIf[],
  options?: TEnumOption[],
  localForm: Record<string, any> = {},
  globalForm: Record<string, any> = {},
) => {
  // Array.isArray rather than a length check: enum_show_if arriving as an object
  // is a plausible authoring mistake, and it must be inert rather than throw.
  if (!Array.isArray(rules) || !rules.length || !options) {
    return options;
  }

  return options.filter(option => rules
    // Rules come from server side schemas, so anything malformed — a null entry,
    // a missing or non-array `values` — has to leave the option unfiltered
    // instead of throwing mid render. Such a rule simply never matches.
    .filter(rule => Array.isArray(rule?.values)
      // Schema enums are strings while option keys may be numbers.
      && rule.values.map(String).includes(String(option.key)))
    .every(rule => showIf(rule.show_if, localForm, globalForm)));
};

// Resolves the value a prop should hold once its options are filtered: the
// current value when still offered, otherwise the schema default when that is
// offered, otherwise nothing.
//
// An empty value is left alone. A oneOf prop that is not `uncheckable` reports
// null when the selected item is clicked again to deselect it, and treating that
// as a stale value would immediately write default_value back and make the field
// impossible to clear.
export const resolveEnumShowIfValue = (
  value: string | number | boolean | null | undefined,
  filteredOptions?: TEnumOption[],
  defaultValue?: string | number | boolean,
) => {
  if (typeof value === 'undefined' || value === null || value === '' || !filteredOptions) {
    return value;
  }

  if (filteredOptions.some(option => String(option.key) === String(value))) {
    return value;
  }

  return filteredOptions.some(option => String(option.key) === String(defaultValue)) ? defaultValue : undefined;
};
